<spinner *ngIf="loading"></spinner>
<section class="h-full flex flex-col overflow-hidden px-6 py-6">

  <h1 class="oswald-font text-3xl text-nside-gray-400">Activity</h1>
  <p class="mt-1 text-sm text-gray-500">View message activity and system events.</p>

  <!-- Row 1: Date range + Type filter + Export (pinned) -->
  <div class="mt-3 flex items-center justify-between rounded-lg bg-white px-5 py-3 shadow-custom-1 shrink-0">
    <div class="flex items-center gap-2">
      <input
        type="date"
        [(ngModel)]="startDate"
        [max]="endDate"
        (change)="onDateRangeChange()"
        class="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
      <span class="text-xs text-gray-400">to</span>
      <input
        type="date"
        [(ngModel)]="endDate"
        [min]="startDate"
        [max]="today"
        (change)="onDateRangeChange()"
        class="rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
      <span class="text-xs text-gray-400 ml-1">|</span>
      <select [(ngModel)]="filterAlertType" (change)="onTypeFilterChange()"
        class="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
        <option value="">All Alert Types</option>
        <option *ngFor="let t of alertTypeOptions" [value]="t[0]">{{ t[1] }}</option>
      </select>
      <select [class.hidden]="!userDataService.isOrg()" [(ngModel)]="filterBuildingId" (ngModelChange)="onBuildingFilterChange()"
        class="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
        <option value="">All Buildings</option>
        <option *ngFor="let b of buildingOptions; trackBy: trackByBuildingId" [value]="b.id">{{ b.name }}</option>
      </select>
    </div>
    <button type="button"
      (click)="exportPdf()"
      class="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm oswald-font text-gray-600 hover:bg-gray-50">
      <lucide-icon [img]="icons.Download" [size]="16"></lucide-icon>
      Export
    </button>
  </div>

  <div class="mt-4 flex-1 overflow-y-auto min-h-0 flex flex-col gap-4 pb-2">

    <!-- Row 2: Charts -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">

      <!-- Incidents Calendar -->
      <div class="rounded-lg bg-white p-5 shadow-custom-1 flex flex-col gap-2">

        <div>
          <!-- Title -->
          <div class="flex items-center gap-1.5">
            <p class="oswald-font text-base text-nside-gray-400">Incidents Calendar</p>
            <div class="relative group">
              <lucide-icon [img]="icons.Info" [size]="14" class="text-gray-400 cursor-help"></lucide-icon>
              <div class="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 hidden group-hover:block w-48 rounded-md bg-gray-700 px-3 py-2 text-xs text-white shadow-lg z-20">
                Monthly view of incidents by type. Navigate months using the arrows.
                <div class="absolute left-1/2 bottom-full -translate-x-1/2 border-4 border-transparent border-b-gray-700"></div>
              </div>
            </div>
          </div>

          <!-- Month nav row -->
          <div class="flex items-center justify-between">
            <span class="text-xs font-medium text-gray-400">{{ calendarMonthLabel }}</span>
            <div class="flex items-center gap-1">
              <button type="button" (click)="prevMonth()"
                class="rounded p-1 hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                <lucide-icon [img]="icons.ChevronLeft" [size]="14"></lucide-icon>
              </button>
              <button type="button" (click)="nextMonth()"
                class="rounded p-1 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rotate-180">
                <lucide-icon [img]="icons.ChevronLeft" [size]="14"></lucide-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- Day-of-week headers -->
        <div class="grid grid-cols-7 gap-1">
          <div *ngFor="let d of weekDays" class="text-center text-[10px] font-medium text-gray-400 py-0.5">
            {{ d }}
          </div>
        </div>

        <!-- Calendar grid -->
        <div *ngFor="let week of calendarGrid" class="grid grid-cols-7 gap-1">
          <div *ngFor="let cell of week"
            class="rounded p-1 min-h-[36px] flex flex-col"
            [ngClass]="cell.day ? cellBgClass(cell) : ''">
            <ng-container *ngIf="cell.day">
              <p class="text-[10px] leading-none text-gray-400">{{ cell.day }}</p>
              <div class="mt-auto flex flex-wrap gap-0.5 pt-1">
                <span *ngIf="cell.lockdown > 0"
                  class="rounded px-0.5 text-[9px] leading-none bg-red-100 text-red-500">{{ cell.lockdown }}</span>
                <span *ngIf="cell.fire > 0"
                  class="rounded px-0.5 text-[9px] leading-none bg-orange-100 text-orange-500">{{ cell.fire }}</span>
                <span *ngIf="cell.police > 0"
                  class="rounded px-0.5 text-[9px] leading-none bg-blue-100 text-blue-500">{{ cell.police }}</span>
                <span *ngIf="cell.medical > 0"
                  class="rounded px-0.5 text-[9px] leading-none bg-green-100 text-green-600">{{ cell.medical }}</span>
                <span *ngIf="cell.administrative > 0"
                  class="rounded px-0.5 text-[9px] leading-none bg-purple-100 text-purple-600">{{ cell.administrative }}</span>
                <span *ngIf="cell.test > 0"
                  class="rounded px-0.5 text-[9px] leading-none bg-gray-200 text-gray-500">{{ cell.test }}</span>
              </div>
            </ng-container>
          </div>
        </div>

        <!-- Legend at bottom -->
        <div class="flex flex-wrap text-xs gap-x-3 gap-y-1 pt-1 border-t border-gray-100">
          <span class="flex items-center gap-1 text-gray-500"><span class="h-2 w-2 rounded-full bg-red-400"></span>Lockdown</span>
          <span class="flex items-center gap-1 text-gray-500"><span class="h-2 w-2 rounded-full bg-orange-400"></span>Fire</span>
          <span class="flex items-center gap-1 text-gray-500"><span class="h-2 w-2 rounded-full bg-blue-400"></span>Police</span>
          <span class="flex items-center gap-1 text-gray-500"><span class="h-2 w-2 rounded-full bg-green-400"></span>Medical</span>
          <span class="flex items-center gap-1 text-gray-500"><span class="h-2 w-2 rounded-full bg-purple-400"></span>Admin</span>
          <span class="flex items-center gap-1 text-gray-500"><span class="h-2 w-2 rounded-full bg-gray-300"></span>Test</span>
        </div>

      </div>

      <!-- Pie/Donut chart: Alert Breakdown -->
      <div class="rounded-lg bg-white p-5 shadow-custom-1 flex flex-col gap-3">

        <!-- Title + info icon -->
        <div class="flex items-start justify-between">
          <div>
            <div class="flex items-center gap-1.5">
              <p class="oswald-font text-base text-nside-gray-400">Messages by Type</p>
              <div class="relative group">
                <lucide-icon [img]="icons.Info" [size]="14" class="text-gray-400 cursor-help mt-0.5"></lucide-icon>
                <div class="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 hidden group-hover:block w-48 rounded-md bg-gray-700 px-3 py-2 text-xs text-white shadow-lg z-20">
                  Breakdown of alert messages sent during the selected period, grouped by type.
                  <div class="absolute left-1/2 bottom-full -translate-x-1/2 border-4 border-transparent border-b-gray-700"></div>
                </div>
              </div>
            </div>
            <p class="text-xs text-gray-400">Distribution by type for selected period</p>
          </div>
        </div>

        <div class="flex flex-1 items-center justify-around gap-4">
          <!-- Donut -->
          <div class="relative shrink-0 w-40 h-40">
            <div class="w-full h-full rounded-full" [style.background]="donutGradient"></div>
            <div class="absolute inset-0 flex flex-col items-center justify-center">
              <div class="w-[96px] h-[96px] rounded-full bg-white flex flex-col items-center justify-center shadow-sm">
                <p class="text-xs text-gray-400 leading-none">Total</p>
                <p class="text-xl font-bold text-nside-gray-400 leading-tight">{{ totalAlerts }}</p>
              </div>
            </div>
          </div>

          <!-- Breakdown list -->
          <div class="flex flex-col gap-3">
            <div class="flex items-start justify-between gap-6"
              *ngFor="let item of [
                { label: 'Lockdown',       color: 'bg-red-400',    count: alertTotals.lockdown       },
                { label: 'Fire',           color: 'bg-orange-400', count: alertTotals.fire           },
                { label: 'Police',         color: 'bg-blue-400',   count: alertTotals.police         },
                { label: 'Medical',        color: 'bg-green-400',  count: alertTotals.medical        },
                { label: 'Administrative', color: 'bg-purple-400', count: alertTotals.administrative },
                { label: 'Test Alert',     color: 'bg-gray-300',   count: alertTotals.test           }
              ]">
              <div class="flex items-start gap-1.5">
                <span class="mt-1 h-2.5 w-2.5 shrink-0 rounded-sm" [ngClass]="item.color"></span>
                <div>
                  <p class="text-sm text-gray-600 leading-tight">{{ item.label }}</p>
                  <p class="text-xs text-gray-400">{{ item.count }} ({{ alertPercent(item.count) }})</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Map -->
      <div class="rounded-lg bg-white p-5 shadow-custom-1 flex flex-col gap-3">
        <div>
          <div class="flex items-center gap-1.5">
            <p class="oswald-font text-base text-nside-gray-400">Message by Location</p>
            <div class="relative group">
              <lucide-icon [img]="icons.Info" [size]="14" class="text-gray-400 cursor-help"></lucide-icon>
              <div class="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 hidden group-hover:block w-48 rounded-md bg-gray-700 px-3 py-2 text-xs text-white shadow-lg z-20">
                Geographic map showing where alerts were triggered across buildings.
                <div class="absolute left-1/2 bottom-full -translate-x-1/2 border-4 border-transparent border-b-gray-700"></div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex-1 min-h-[200px]">
          <app-google-map [markers]="mapMarkers" class="block h-full"></app-google-map>
        </div>
      </div>

    </div>

    <!-- Row 3: Tables -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">

      <!-- Activity Log table -->
      <div class="lg:col-span-2 rounded-lg bg-white p-5 shadow-custom-1 flex flex-col gap-3">
        <div class="flex items-center justify-between gap-3">
          <p class="oswald-font text-base text-nside-gray-400 shrink-0">Activity Log</p>
          <div class="relative w-44">
            <div class="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-2.5">
              <lucide-icon [img]="icons.Search" [size]="13" class="text-gray-400"></lucide-icon>
            </div>
            <input type="search" [(ngModel)]="alertsSearch" (input)="filterAlerts()"
              placeholder="Search activity..."
              class="w-full rounded border border-gray-300 bg-gray-50 py-1 ps-8 pe-2 text-xs text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
          </div>
        </div>

        <div class="overflow-x-auto">
        <table class="w-full text-left text-sm">
          <thead class="oswald-font text-xs uppercase text-gray-500 bg-main-background">
            <tr>
              <th class="px-3 py-2 whitespace-nowrap">
                <button type="button" (click)="sortAlerts('dateSubmitted')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Date &amp; Time <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
              <th *ngIf="userDataService.isOrg()" class="px-3 py-2 whitespace-nowrap">
                <button type="button" (click)="sortAlerts('alertBuildingName')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Building <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
              <th class="px-3 py-2">
                <button type="button" (click)="sortAlerts('alertTypeId')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Type <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
              <th class="px-3 py-2 whitespace-nowrap">
                <button type="button" (click)="sortAlerts('userNameSubmitted')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Sender <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
              <th class="px-3 py-2 whitespace-nowrap">
                <button type="button" (click)="sortAlerts('buildingLevelName')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Level <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
              <th class="px-3 py-2 whitespace-nowrap">
                <button type="button" (click)="sortAlerts('alertReceiverTypeId')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Status
                </button>
              </th>
              <th class="px-3 py-2 whitespace-nowrap">
                <button type="button" (click)="sortAlerts('alertReceiverTypeId')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Receiver <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let alert of filteredAlerts | paginate: { id: 'alerts', itemsPerPage: tableItemsPerPage, currentPage: alertsPage }"
              class="border-b bg-white hover:bg-gray-50">
              <td class="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{{ alert.dateSubmitted | date:'MM/dd/yyyy - HH:mm' }}</td>
              <td *ngIf="userDataService.isOrg()" class="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{{ alert.alertBuildingName }}</td>
              <td class="px-3 py-2.5">
                <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="alertTypeClass(alert.alertTypeId)">
                  {{ alertTypeName(alert.alertTypeId) }}
                </span>
              </td>
              <td class="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{{ alert.userNameSubmitted }}</td>
              <td class="px-3 py-2.5 text-xs text-gray-600">{{ alert.buildingLevelName ?? '—' }}</td>
              <td class="px-3 py-2.5">
                <button type="button" (click)="openAlertStatuses(alert)"
                  class="flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 hover:border-gray-300 whitespace-nowrap">
                  <lucide-icon [img]="icons.Users" [size]="12"></lucide-icon>
                  Statuses
                </button>
              </td>
              <td class="px-3 py-2.5">
                <button type="button" (click)="openAlertRecipients(alert)"
                  class="flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 hover:border-gray-300 whitespace-nowrap">
                  <lucide-icon [img]="icons.Mail" [size]="12"></lucide-icon>
                  Recipients
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        <div class="flex items-center justify-between">
          <p class="text-xs text-gray-400">{{ filteredAlerts.length }} in total</p>
          <pagination-controls
            id="alerts"
            *ngIf="filteredAlerts.length > tableItemsPerPage"
            (pageChange)="alertsPage = $event"
            previousLabel="Prev"
            nextLabel="Next">
          </pagination-controls>
        </div>
      </div>

      <!-- Status Log table (mirrors user_statuses table) -->
      <div class="rounded-lg bg-white p-5 shadow-custom-1 flex flex-col gap-3">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <p class="oswald-font text-base text-nside-gray-400 shrink-0">Status Log</p>
          <div class="flex flex-wrap items-center gap-2 min-w-0">
            <select [(ngModel)]="filterStatusType" (change)="filterResponses()"
              class="rounded border border-gray-300 bg-gray-50 py-1 px-2 text-xs text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
              <option value="">All Statuses</option>
              <option *ngFor="let s of statusTypeOptions" [value]="s[0]">{{ s[1] }}</option>
            </select>
            <div class="relative flex-1 min-w-32">
              <div class="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-2.5">
                <lucide-icon [img]="icons.Search" [size]="13" class="text-gray-400"></lucide-icon>
              </div>
              <input type="search" [(ngModel)]="responsesSearch" (input)="filterResponses()"
                placeholder="Search response..."
                class="w-full rounded border border-gray-300 bg-gray-50 py-1 ps-8 pe-2 text-xs text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        <div class="overflow-x-auto">
        <table class="w-full text-left text-sm">
          <thead class="oswald-font text-xs uppercase text-gray-500 bg-main-background">
            <tr>
              <th class="px-3 py-2">
                <button type="button" (click)="sortResponses('userName')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  User <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
              <th class="px-3 py-2">
                <button type="button" (click)="sortResponses('alertId')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Alert <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
              <th class="px-3 py-2">
                <button type="button" (click)="sortResponses('userStatus')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Status <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
              <th *ngIf="userDataService.isOrg()" class="px-3 py-2 whitespace-nowrap">Building</th>
              <th class="px-3 py-2 whitespace-nowrap">
                <button type="button" (click)="sortResponses('dateTimeSubmitted')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  Time <lucide-icon [img]="icons.ArrowUpDown" [size]="12"></lucide-icon>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let r of filteredResponses | paginate: { id: 'responses', itemsPerPage: tableItemsPerPage, currentPage: responsesPage }"
              class="border-b bg-white hover:bg-gray-50">
              <td class="px-3 py-2.5 text-xs font-medium text-gray-900 whitespace-nowrap">{{ r.userName }}</td>
              <td class="px-3 py-2.5 whitespace-nowrap">
                <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="alertTypeClass(alertTypeIdForAlert(r.alertId))">
                  {{ alertTypeName(alertTypeIdForAlert(r.alertId)) }}
                </span>
              </td>
              <td class="px-3 py-2.5 whitespace-nowrap">
                <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="responseClass(r.userStatus)">
                  {{ r.userStatusName }}
                </span>
              </td>
              <td *ngIf="userDataService.isOrg()" class="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                {{ alertBuildingForResponse(r.alertId)?.name ?? '—' }}
              </td>
              <td class="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{{ (r.dateTimeSubmitted ? r.dateTimeSubmitted + 'Z' : '') | date:'MM/dd/yyyy - HH:mm' }}</td>
            </tr>
          </tbody>
        </table>
        </div>

        <div class="flex items-center justify-between">
          <p class="text-xs text-gray-400">{{ filteredResponses.length }} in total</p>
          <pagination-controls
            id="responses"
            *ngIf="filteredResponses.length > tableItemsPerPage"
            (pageChange)="responsesPage = $event"
            previousLabel="Prev"
            nextLabel="Next">
          </pagination-controls>
        </div>
      </div>

    </div>

  </div>
</section>

<app-alert-statuses-modal
  *ngIf="selectedAlert"
  [alert]="selectedAlert"
  [statuses]="alertStatuses"
  [loading]="loadingStatuses"
  (closed)="closeAlertStatuses()">
</app-alert-statuses-modal>

<app-alert-recipients-modal
  *ngIf="selectedAlertForRecipients"
  [alert]="selectedAlertForRecipients"
  [recipients]="alertRecipients"
  [loading]="loadingRecipients"
  (closed)="closeAlertRecipients()">
</app-alert-recipients-modal>
